/**
 * Batch 18-D8 — Training Recommendation Engine Explanation Helpers.
 *
 * Pure helpers that produce the operator-visible "Why you're seeing
 * this" string (`generateWhyShowing`) and the legacy
 * "Recommended because: ..." string (`generateExplanation`) for
 * artifacts ranked by `trainingRecommendationEngine.scoreArtifacts`.
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/trainingRecommendationEngine.ts` prior to Batch 18-D8.
 * Every output string — including the fallback copy, the
 * domain-key reformatting, the clause separator, and the
 * cap-at-2-clauses rule — is identical to the prior engine version.
 *
 * Operator-facing copy (Spec-pinned; do NOT edit casually):
 *   - `Matches your <domain> focus`     (when factors.domainRelevance > 0.5)
 *   - `Right for your current skill level`  (when factors.levelMatch > 0.2)
 *   - `Fits your learning style`        (when factors.styleMatch > 0.2)
 *   - `OptiBay verified quality`        (when confidence_level === 'optibay_verified')
 *   - `Quick watch`                     (when estimated_time_minutes <= 15)
 *   - `Recommended based on general relevance`  (fallback)
 *   - `Recommended because: <clauses>`  (legacy explanation prefix)
 *   - Clauses are joined by ` • ` (space-bullet-space)
 *   - `generateWhyShowing` caps output at the first 2 clauses
 *
 * The two helpers are independent (no inter-dependency) but live in
 * the same module because they share the engine-side concept of
 * "explaining a scored recommendation". They take a `ScoredArtifact`
 * as input, imported as TYPE ONLY from the engine. TypeScript elides
 * type-only imports at compile time, so there is no runtime module
 * cycle: at runtime, only the engine imports from this file.
 *
 * Out of scope:
 *   - The ticket-side reason helper
 *     (`@/utils/trainingRecommendationReasons`) is a separate operator
 *     surface with its own frozen copy map. It is NOT touched by 18-D8.
 *   - The contextual scorer's private `generateExplanation`
 *     (`contextualTrainingScorer.ts:310`) is a separately-scoped
 *     function with the same name. NOT touched by 18-D8.
 */

import type { ScoredArtifact } from '@/utils/trainingRecommendationEngine';

/**
 * Generate human-readable "Why you're seeing this" explanation
 */
export function generateWhyShowing(scored: ScoredArtifact): string {
  const parts: string[] = [];
  const { factors, artifact } = scored;

  // Domain relevance
  if (factors.domainRelevance > 0.5) {
    const primaryDomain = artifact.domains?.find(d => d.is_primary)?.domain_key;
    if (primaryDomain) {
      parts.push(`Matches your ${primaryDomain.replace(/_/g, ' ')} focus`);
    }
  }

  // Level match
  if (factors.levelMatch > 0.2) {
    parts.push('Right for your current skill level');
  }

  // Style match
  if (factors.styleMatch > 0.2) {
    parts.push('Fits your learning style');
  }

  // Confidence level
  if (artifact.confidence_level === 'optibay_verified') {
    parts.push('OptiBay verified quality');
  }

  // Duration-based
  if (artifact.estimated_time_minutes && artifact.estimated_time_minutes <= 15) {
    parts.push('Quick watch');
  }

  if (parts.length === 0) {
    return 'Recommended based on general relevance';
  }

  return parts.slice(0, 2).join(' • ');
}

/**
 * Generate human-readable explanation for a recommendation
 * @deprecated Use whyShowing property on ScoredArtifact instead
 */
export function generateExplanation(scored: ScoredArtifact): string {
  const parts = scored.explanation.filter(Boolean);

  if (parts.length === 0) {
    return 'Recommended based on general relevance';
  }

  return `Recommended because: ${parts.join(' • ')}`;
}
